import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Handshake, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { formatApiError } from "../lib/auth";
import { toast } from "sonner";

export const MakeOfferDialog = ({ product, open, onOpenChange }) => {
  const [step, setStep] = useState("form"); // form | success
  const [submittedOffer, setSubmittedOffer] = useState(null);
  const [form, setForm] = useState({
    offered_price: "",
    buyer_name: "",
    buyer_email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setStep("form");
    setForm({ offered_price: "", buyer_name: "", buyer_email: "", message: "" });
    setError("");
    setSubmittedOffer(null);
  };

  const handleClose = (open) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/offers", {
        product_id: product.id,
        buyer_name: form.buyer_name,
        buyer_email: form.buyer_email,
        offered_price: parseFloat(form.offered_price),
        message: form.message,
      });
      setSubmittedOffer(data);
      setStep("success");
      toast.success("Offer submitted!");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Submission failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const listed = product?.price || 0;
  const offered = parseFloat(form.offered_price) || 0;
  const discount = offered > 0 && offered < listed
    ? Math.round(((listed - offered) / listed) * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="rounded-none border-2 border-zinc-950 max-w-lg"
        data-testid="make-offer-dialog"
      >
        {step === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
                <Handshake size={24} weight="duotone" />
                MAKE AN OFFER
              </DialogTitle>
              <DialogDescription className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">
                Propose your price for {product?.name}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 py-2">
              <div className="border border-zinc-200 p-3 flex items-center gap-3">
                <img
                  src={product?.image}
                  alt={product?.name}
                  className="w-14 h-14 object-cover border border-zinc-200"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500">
                    Listed price
                  </div>
                  <div className="font-mono text-xl font-bold">${listed.toFixed(2)}</div>
                </div>
              </div>

              <div>
                <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
                  Your offer (USD)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={form.offered_price}
                  onChange={(e) => setForm({ ...form, offered_price: e.target.value })}
                  className="rounded-none border-zinc-300 h-12 font-mono text-lg font-bold"
                  placeholder="0.00"
                  data-testid="offer-price-input"
                />
                {discount > 0 && (
                  <div className="font-mono text-xs uppercase tracking-wider text-[#FF2A00] mt-1">
                    {discount}% below listed price
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
                    Name
                  </label>
                  <Input
                    type="text"
                    required
                    value={form.buyer_name}
                    onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                    className="rounded-none border-zinc-300 font-mono text-sm"
                    data-testid="offer-name-input"
                  />
                </div>
                <div>
                  <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    required
                    value={form.buyer_email}
                    onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                    className="rounded-none border-zinc-300 font-mono text-sm"
                    data-testid="offer-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
                  Message (optional)
                </label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-none border-zinc-300 font-mono text-sm"
                  rows={2}
                  placeholder="Cash buyer, bundle deal, etc."
                  maxLength={500}
                  data-testid="offer-message-input"
                />
              </div>

              {error && (
                <div
                  className="border border-[#FF2A00] bg-red-50 text-[#FF2A00] px-3 py-2 font-mono text-xs uppercase tracking-wider"
                  data-testid="offer-error"
                >
                  {error}
                </div>
              )}

              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 leading-relaxed">
                Minimum offer: 30% of listed price. Admin will review and respond by email.
              </p>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  className="rounded-none"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-offer-button"
                  className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
                >
                  {loading ? "Sending..." : "Send Offer"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="offer-success">
            <CheckCircle size={48} weight="duotone" className="mx-auto mb-3 text-green-600" />
            <h3 className="font-heading font-black text-2xl tracking-tight mb-2">
              Offer submitted!
            </h3>
            <p className="text-sm text-zinc-700 mb-4">
              We sent your offer of{" "}
              <span className="font-mono font-bold">
                ${submittedOffer?.offered_price.toFixed(2)}
              </span>{" "}
              to the admin. You'll hear back at{" "}
              <span className="font-mono">{submittedOffer?.buyer_email}</span>.
            </p>
            <div className="border-y border-zinc-200 py-3 my-4 grid grid-cols-2 gap-2 text-left">
              <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500">
                Offer ID
              </div>
              <div className="font-mono text-xs text-right">
                {submittedOffer?.id.slice(0, 8)}…
              </div>
              <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500">
                Status
              </div>
              <div className="font-mono text-xs text-right uppercase">Pending</div>
            </div>
            <Button
              onClick={() => handleClose(false)}
              className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
              data-testid="offer-done-button"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
